import * as React from "react";
import { Slot } from "@radix-ui/react-slot";

import { FOCUS_RING_INSET_AFTER } from "../lib/focus";
import { cn } from "../lib/utils";
import { Icon, type IconComponent } from "./icon";

/**
 * v2 List + ListItem (redesign phase 5, #455): one row for the chat list, the
 * Service Assistant list and similar master lists (break #101). Storybook only
 * until implementation. TreeView builds on it later.
 *
 * Row: `min-h-10 py-2 px-4 gap-3`. Title Sora 14 Semibold `--ink` (one line),
 * description Inter 14 `--ink-muted` clamped to 1 or 2 lines, meta (a time)
 * Sora 12 `--ink-muted` tabular top right, trailing (a Badge) under it, a
 * leading icon or avatar node left.
 *
 * Selected: `--primary-subtle` with the title in `--primary-strong` (light,
 * like an active NavItem) or `--ink` (dark: `--primary-strong` drops to
 * 7.43:1 there, `--ink` holds 11.13; the fill already marks the row; break
 * #173), and `aria-current` on the link. No hover on it.
 * Unread: an 8px `--primary-accent` dot before the time (5.45 / 4.93 on card,
 * 4.75 / 3.62 on selected) and "Ungelesen" as screen-reader text in the name.
 * A dot, not a left bar: a bar collides with the selected tint and the focus
 * ring and reads as selection.
 *
 * Click target: the title is the link (or button), and its `::after` covers
 * the whole row, so the row is clickable while the name stays the title. The
 * focus ring is drawn on that overlay, inset (rows sit flush in cards).
 *
 * Row actions (`actions`, usually the "Weitere Aktionen" kebab) sit beside
 * the link, above the overlay: a separate tab stop, never nested. Hidden until
 * the row is hovered, holds focus, is selected, has its menu open, or the
 * pointer is coarse (touch has no hover); the 28px slot is reserved, so nothing shifts. Tables
 * keep their actions always visible; lists hide them because a narrow column
 * would lose the title's width to a kebab on every row.
 */

/** Row hover, shared with TreeView (same look). */
export const LIST_ROW_HOVER = "hover:bg-hover-overlay data-hover:bg-hover-overlay";
/**
 * Selected row fill, shared with TreeView and ConversationSearchResults. A
 * selected row is `group/item` + `data-selected`: a neutral Badge inside it
 * keys on that to draw its 1px ring (its `--muted` fill is 1.07 / 1.04:1
 * against `--primary-subtle`; break #173). The active NavItem uses the same
 * pair; a linked-highlight row (FactCard, SourceCard) is `group/item` +
 * `data-highlighted`, which the Badge also keys on (break #188).
 */
export const LIST_ROW_SELECTED = "bg-primary-subtle";
/** Title (and icon) text on a selected row: `--primary-strong` light, `--ink` dark (break #173). */
export const LIST_ROW_SELECTED_TEXT = "text-primary-strong dark:text-ink";
/**
 * The kebab's reveal rule, shared with TreeView (rows are `group/item`):
 * hidden until the row is hovered or holds focus, its menu is open
 * (has-aria-expanded: the open menu holds focus in its portal, so
 * focus-within ends; aria-expanded, not data-state, which the IconButton's
 * tooltip trigger overrides), or the pointer is coarse (touch has no hover).
 * A selected row shows it always (the caller swaps in `opacity-100`).
 */
export const LIST_ROW_ACTIONS_REVEAL =
  "opacity-0 group-hover/item:opacity-100 group-focus-within/item:opacity-100 group-data-hover/item:opacity-100 group-has-data-focus/item:opacity-100 has-aria-expanded:opacity-100 pointer-coarse:opacity-100";

export interface ListProps extends React.HTMLAttributes<HTMLUListElement> {
  /** `--divider` lines between rows. Default true. */
  divided?: boolean;
}

const List = React.forwardRef<HTMLUListElement, ListProps>(function List(
  { divided = true, className, ...props },
  ref,
) {
  return (
    <ul ref={ref} role="list" className={cn("flex flex-col", divided && "divide-y divide-divider", className)} {...props} />
  );
});

export interface ListItemProps extends Omit<React.HTMLAttributes<HTMLElement>, "title" | "children"> {
  title: React.ReactNode;
  /** 2 wraps a long title instead of cutting it (a ticket subject told apart by its last words). */
  titleLines?: 1 | 2;
  description?: React.ReactNode;
  descriptionLines?: 1 | 2;
  /** A quote of the content under the description, Inter 12 `--ink-muted`, at most 2 lines. */
  excerpt?: React.ReactNode;
  /** Top right, e.g. the time "09:41". */
  meta?: React.ReactNode;
  /** Under the meta, e.g. a Badge. */
  trailing?: React.ReactNode;
  /** An avatar or other node; `leadingIcon` for a plain 16px icon. */
  leading?: React.ReactNode;
  leadingIcon?: IconComponent;
  selected?: boolean;
  unread?: boolean;
  /** Screen-reader text for `unread`. German default. */
  unreadLabel?: string;
  /** The row's actions, usually one kebab DropdownMenu. */
  actions?: React.ReactNode;
  /** Render the single child element (a router link) as the row's link. */
  asChild?: boolean;
  children?: React.ReactElement;
  /** Static states for stories; apps never set them. */
  "data-hover"?: string;
}

const ListItem = React.forwardRef<HTMLElement, ListItemProps>(function ListItem(
  {
    title,
    titleLines = 1,
    description,
    descriptionLines = 1,
    excerpt,
    meta,
    trailing,
    leading,
    leadingIcon,
    selected = false,
    unread = false,
    unreadLabel = "Ungelesen",
    actions,
    asChild = false,
    className,
    children,
    "data-hover": dataHover,
    ...props
  },
  ref,
) {
  const label = (
    <>
      {unread && (
        <>
          <span className="sr-only">{unreadLabel}:</span>{" "}
        </>
      )}
      {title}
    </>
  );

  const shared = {
    ...props,
    ref,
    "aria-current": selected ? ("true" as const) : undefined,
    className: cn(
      "min-w-0 text-left font-sans text-label outline-hidden",
      titleLines === 2 ? "line-clamp-2" : "truncate",
      selected ? LIST_ROW_SELECTED_TEXT : "text-ink",
      // The overlay makes the whole row the target; the ring is drawn on it.
      "cursor-pointer after:absolute after:inset-0",
      FOCUS_RING_INSET_AFTER,
      className,
    ),
  };

  const main =
    asChild && React.isValidElement(children) ? (
      <Slot {...shared}>{React.cloneElement(children, undefined, label)}</Slot>
    ) : (
      <button {...shared} ref={ref as React.Ref<HTMLButtonElement>} type="button">
        {label}
      </button>
    );

  return (
    <li
      data-selected={selected || undefined}
      data-hover={dataHover}
      className={cn(
        "group/item relative flex min-h-10 items-start gap-3 px-4 py-2 transition-colors",
        selected ? LIST_ROW_SELECTED : LIST_ROW_HOVER,
      )}
    >
      {(leading || leadingIcon) && (
        <div className="flex min-h-5 shrink-0 items-center text-ink-muted">
          {leading ?? (leadingIcon && <Icon icon={leadingIcon} />)}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {main}
        {description != null && (
          <p
            className={cn(
              "font-body text-body text-ink-muted",
              descriptionLines === 2 ? "line-clamp-2" : "line-clamp-1",
            )}
          >
            {description}
          </p>
        )}
        {excerpt != null && <p className="font-body text-caption text-ink-muted line-clamp-2">{excerpt}</p>}
      </div>
      {(meta != null || trailing != null || unread) && (
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className="flex min-h-5 items-center gap-1.5">
            {unread && <span aria-hidden="true" data-slot="unread-dot" className="size-2 rounded-full bg-primary-accent" />}
            {meta != null && <span className="font-sans text-caption text-ink-muted tabular-nums">{meta}</span>}
          </div>
          {trailing}
        </div>
      )}
      {actions != null && (
        <div
          data-slot="actions"
          className={cn(
            // Above the row overlay. Reserved width; only the opacity changes.
            "relative z-10 flex size-7 shrink-0 items-center justify-center self-center transition-opacity",
            selected ? "opacity-100" : LIST_ROW_ACTIONS_REVEAL,
          )}
        >
          {actions}
        </div>
      )}
    </li>
  );
});

export { List, ListItem };
